import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import MobileNav from "@/components/MobileNav";
import {
  ArrowLeft,
  Send,
  Camera,
  Info,
  DollarSign,
  Image,
  CheckCircle,
  Clock,
  Loader2,
  Upload,
  X
} from "lucide-react";

const SignalsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [message, setMessage] = useState("");
  const [uploadedImage, setUploadedImage] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const boardRes = await axios.get(`${API}/board/current`, { withCredentials: true });
      if (boardRes.data) {
        setBoard(boardRes.data);
        // Fetch signals for this board
        const signalsRes = await axios.get(`${API}/signals/${boardRes.data.board_id}`, { withCredentials: true });
        setSignals(signalsRes.data || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const signalTypes = [
    {
      id: "inspiration",
      icon: Image,
      label: "Share Inspiration",
      desc: "Send a photo for reference",
      color: "bg-purple-100 text-purple-700",
      allowPhoto: true
    },
    {
      id: "price_question",
      icon: DollarSign,
      label: "Price Question",
      desc: "Ask about pricing (attach photo for specific quote)",
      color: "bg-green-100 text-green-700",
      allowPhoto: true
    },
    {
      id: "clarification",
      icon: Info,
      label: "Clarification",
      desc: "Ask a question about your service",
      color: "bg-blue-100 text-blue-700",
      allowPhoto: false
    }
  ];

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage({
          file,
          preview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSendSignal = async () => {
    if (!board || !selectedType) {
      toast.error("Please select a signal type");
      return;
    }

    if (selectedType === "inspiration" && !uploadedImage) {
      toast.error("Please upload an image");
      return;
    }

    if ((selectedType === "price_question" || selectedType === "clarification") && !message.trim()) {
      toast.error("Please enter your question");
      return;
    }

    setSending(true);
    try {
      const signalData = {
        board_id: board.board_id,
        signal_type: selectedType,
        message: message.trim() || null,
        image_url: uploadedImage?.preview || null // In production, upload to server first
      };

      await axios.post(`${API}/signals`, signalData, { withCredentials: true });
      
      toast.success("Signal sent to staff!");
      setSelectedType(null);
      setMessage("");
      setUploadedImage(null);
      fetchData(); // Refresh signals
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not send signal");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
      </div>
    );
  }

  if (!board) {
    return (
      <div className="min-h-screen bg-white pb-32">
        <header className="border-b border-gray-100 sticky top-0 z-40 bg-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
            <Link to="/wallet" className="text-gray-500 hover:text-black">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-display text-xl font-bold">Signals</h1>
          </div>
        </header>
        
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Send className="w-8 h-8 text-gray-500" />
            </div>
            <h2 className="font-display text-xl font-semibold mb-2">No Active Appointment</h2>
            <p className="text-gray-600 mb-6">Check in at a venue to send signals to staff</p>
            <Link to="/wallet">
              <Button className="bg-black hover:bg-gray-900 text-white rounded-full">
                Go to Wallet
              </Button>
            </Link>
          </div>
        </main>
        
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/wallet" className="text-gray-500 hover:text-black">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-display text-xl font-bold">Signals</h1>
              <p className="text-xs text-gray-600">Send to staff</p>
            </div>
          </div>
          <Badge variant="outline">
            {signals.length} sent
          </Badge>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
          <p className="text-sm text-blue-800">
            <strong>Signals</strong> let you communicate with staff without back-and-forth chat. 
            Send an inspiration image or ask a quick question.
          </p>
        </div>

        {/* Signal Type Selection */}
        <section>
          <h2 className="font-display text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">
            What would you like to send?
          </h2>
          <div className="space-y-3">
            {signalTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => {
                  setSelectedType(type.id);
                  setMessage("");
                  setUploadedImage(null);
                }}
                className={`w-full bg-white rounded-2xl p-4 border-2 transition-all flex items-center gap-4 text-left ${
                  selectedType === type.id 
                    ? "border-black" 
                    : "border-gray-100 hover:border-gray-300"
                }`}
              >
                <div className={`w-12 h-12 rounded-xl ${type.color} flex items-center justify-center`}>
                  <type.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{type.label}</p>
                  <p className="text-sm text-gray-600">{type.desc}</p>
                </div>
                {selectedType === type.id && (
                  <CheckCircle className="w-5 h-5 text-black" />
                )}
              </button>
            ))}
          </div>
        </section>

        {/* Signal Input */}
        {selectedType && (
          <section className="bg-white rounded-2xl p-4 border border-gray-100">
            {selectedType === "inspiration" ? (
              <div className="space-y-4">
                <p className="font-medium">Upload an inspiration image</p>
                
                {uploadedImage ? (
                  <div className="relative">
                    <img 
                      src={uploadedImage.preview} 
                      alt="Upload preview" 
                      className="w-full h-48 object-cover rounded-xl"
                    />
                    <button
                      onClick={() => setUploadedImage(null)}
                      className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="block">
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-gray-400 transition-colors">
                      <Camera className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                      <p className="text-sm text-gray-700">Click to upload or take a photo</p>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                  </label>
                )}
                
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a note about this image (optional)"
                  className="resize-none"
                  rows={2}
                />
              </div>
            ) : selectedType === "price_question" ? (
              <div className="space-y-4">
                <p className="font-medium">What would you like to know about pricing?</p>
                
                {/* Optional Photo for Price Question */}
                <div className="bg-green-50 border border-green-100 rounded-xl p-3 mb-2">
                  <p className="text-xs text-green-700">
                    💡 Attach a photo of your inspiration to get a specific price quote
                  </p>
                </div>
                
                {uploadedImage ? (
                  <div className="relative">
                    <img 
                      src={uploadedImage.preview} 
                      alt="Upload preview" 
                      className="w-full h-32 object-cover rounded-xl"
                    />
                    <button
                      onClick={() => setUploadedImage(null)}
                      className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="block">
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-gray-400 transition-colors">
                      <div className="flex items-center justify-center gap-2 text-gray-600">
                        <Camera className="w-5 h-5" />
                        <span className="text-sm">Attach photo (optional)</span>
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                  </label>
                )}
                
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g., How much extra for nail art like this on two nails?"
                  className="resize-none"
                  rows={3}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <p className="font-medium">What would you like to clarify?</p>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g., Can you do a slightly shorter length than shown?"
                  className="resize-none"
                  rows={3}
                />
              </div>
            )}
            
            <Button
              onClick={handleSendSignal}
              disabled={sending}
              className="w-full mt-4 bg-black hover:bg-gray-900 text-white rounded-xl h-12"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Signal
                </>
              )}
            </Button>
          </section>
        )}

        {/* Previous Signals */}
        {signals.length > 0 && (
          <section>
            <h2 className="font-display text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">
              Sent Signals
            </h2>
            <div className="space-y-3">
              {signals.map((signal) => (
                <div 
                  key={signal.signal_id} 
                  className="bg-white rounded-2xl p-4 border border-gray-100"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      signal.signal_type === "inspiration" ? "bg-purple-100" :
                      signal.signal_type === "price_question" ? "bg-green-100" :
                      "bg-blue-100"
                    }`}>
                      {signal.signal_type === "inspiration" ? <Image className="w-5 h-5 text-purple-600" /> :
                       signal.signal_type === "price_question" ? <DollarSign className="w-5 h-5 text-green-600" /> :
                       <Info className="w-5 h-5 text-blue-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm capitalize">
                          {signal.signal_type.replace("_", " ")}
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {signal.status === "pending" ? (
                            <><Clock className="w-3 h-3 mr-1" />Pending</>
                          ) : (
                            <><CheckCircle className="w-3 h-3 mr-1" />Seen</>
                          )}
                        </Badge>
                      </div>
                      {signal.message && (
                        <p className="text-sm text-gray-700">{signal.message}</p>
                      )}
                      {signal.image_url && (
                        <img 
                          src={signal.image_url} 
                          alt="Signal" 
                          className="mt-2 w-24 h-24 object-cover rounded-lg"
                        />
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(signal.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  
                  {/* Staff Response */}
                  {signal.staff_response && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-600 mb-1">Staff Response:</p>
                      <p className="text-sm text-gray-800">{signal.staff_response}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <MobileNav />
    </div>
  );
};

export default SignalsPage;
